/**
 * EventDetail - 事件详情页面
 */

import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { eventsApi } from '../api/events'
import { useEvent } from '../hooks/useEvents'
import { useLanguage } from '../hooks/useLanguage'
import { InterpretView } from '../components/InterpretView'
import { PolymarketCard } from '../components/PolymarketCard'
import { FollowButton } from '../components/FollowButton'
import { AnalyticsTracker } from '../components/AnalyticsTracker'
import { DetailLoadingState, DetailErrorState } from '../components/StateComponents'
import { getNarrativeConfig } from '../utils/category'
import { getTweetLink } from '../utils/format'
import { requireCredit } from '../utils/billing'
import WebApp from '@twa-dev/sdk'
import { Scale } from 'lucide-react'
import { trackBusinessEvent } from '../utils/analytics'

// 格式化数字
function formatNum(num: number): string {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
  return num.toString()
}

export function EventDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [navigatingDirection, setNavigatingDirection] = useState<'prev' | 'next' | null>(null)
  const { lang, t } = useLanguage()

  // 从 URL 参数获取日期和筛选条件
  const date = searchParams.get('date') || 'latest'
  const narrative = searchParams.get('narrative') || undefined
  const currentIndex = searchParams.get('index') ? parseInt(searchParams.get('index')!) : undefined
  const totalInCategory = searchParams.get('total') ? parseInt(searchParams.get('total')!) : undefined
  const sourcePage = searchParams.get('from')
  const handleBack = () => {
    if (sourcePage === 'notifications') {
      navigate('/notifications')
      return
    }
    navigate('/')
  }

  const { data: event, isLoading, error } = useEvent(id, date)
  const [interpretData] = useState<{ interpretations: Record<string, any> } | null>(null)
  const interpretLoading = false

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [id])

  // 获取解读数据
  /*
    queryKey: ['interpret', id],
    
    }),
    enabled: false, // 手动触发
  }) */

  // 获取 Polymarket 匹配
  const { data: polymarketData } = useQuery({
    queryKey: ['polymarket', id],
    queryFn: () => eventsApi.getPolymarket(id || ''),
    enabled: !!id,
  })

  if (isLoading) {
    return <DetailLoadingState />
  }

  if (error || !event) {
    return <DetailErrorState onBack={handleBack} />
  }

  const config = getNarrativeConfig(event.narrative)

  // 生成解读并触发埋点
  const handleOpenExplain = () => {
    trackBusinessEvent('detail_explain_click', {
      event_id: id,
      narrative: narrative || event.narrative,
      index: currentIndex,
      total: totalInCategory,
      source_page: 'event_detail',
    }, id)
    const params = new URLSearchParams({
      date,
      from: 'detail',
    })

    if (narrative) params.set('narrative', narrative)
    if (currentIndex !== undefined) params.set('index', String(currentIndex))
    if (totalInCategory !== undefined) params.set('total', String(totalInCategory))

    navigate(`/event/${id}/explain?${params.toString()}`)
  }

  const handleOpenPolicyAnalysis = () => {
    if (!id) return
    trackBusinessEvent('policy_analysis_click', {
      event_id: id,
      narrative: narrative || event.narrative,
      source_page: 'event_detail',
    }, id)
    const params = new URLSearchParams({ date, from: 'detail' })
    if (narrative) params.set('narrative', narrative)
    navigate(`/event/${id}/policy-analysis?${params.toString()}`, { state: { event } })
  }

  const handleOpenTimeline = async () => {
    if (!id) return
    trackBusinessEvent('detail_timeline_click', {
      event_id: id,
      narrative: narrative || event.narrative,
      index: currentIndex,
      total: totalInCategory,
      source_page: 'event_detail',
    }, id)

    const ok = await requireCredit(
      'view_timeline',
      id,
      t('查看时间轴', 'View Timeline'),
      (message) => WebApp.showAlert(message)
    )
    if (!ok) return

    const params = new URLSearchParams({
      date,
      from: 'detail',
    })
    if (narrative) params.set('narrative', narrative)
    if (currentIndex !== undefined) params.set('index', String(currentIndex))
    if (totalInCategory !== undefined) params.set('total', String(totalInCategory))

    navigate(`/event/${id}/timeline-chat?${params.toString()}`)
  }

  // 导航到上一个/下一个事件
  const navigateToEvent = async (direction: 'prev' | 'next') => {
    if (currentIndex === undefined || totalInCategory === undefined || navigatingDirection) return

    const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1
    if (newIndex < 0 || newIndex >= totalInCategory) return
    trackBusinessEvent(direction === 'next' ? 'detail_next_click' : 'detail_prev_click', {
      event_id: id,
      narrative: narrative || event.narrative,
      from_index: currentIndex,
      to_index: newIndex,
      total: totalInCategory,
    }, id)

    // 这里需要从列表页传递所有事件 ID，或者重新请求
    // 暂时使用返回列表的方式
    const limit = 20
    const page = Math.floor(newIndex / limit) + 1
    const indexInPage = newIndex % limit
    const listNarrative = narrative || event.narrative

    setNavigatingDirection(direction)
    try {
      const ok = await requireCredit(
        direction === 'next' ? 'detail_next' : 'detail_prev',
        id || '',
        direction === 'next' ? t('详情下一页', 'Next Detail') : t('详情上一页', 'Previous Detail'),
        (message) => WebApp.showAlert(message)
      )
      if (!ok) return

      const eventsData = await eventsApi.getEvents({
        date,
        narrative: listNarrative,
        page,
        limit,
        lang,
      })
      const nextEvent = eventsData.events[indexInPage]

      if (!nextEvent) return

      navigate(`/event/${nextEvent.event_id}?date=${date}&narrative=${listNarrative || ''}&index=${newIndex}&total=${eventsData.total}`)
    } finally {
      setNavigatingDirection(null)
    }
  }

  const canGoPrev = currentIndex !== undefined && currentIndex > 0
  const canGoNext = currentIndex !== undefined && totalInCategory !== undefined && currentIndex < totalInCategory - 1

  return (
    <AnalyticsTracker
      eventId={id || ''}
      date={date}
      enabled={!!id}
    >
      <div className="min-h-screen bg-[var(--tg-bg-color,#f5f5f5)] pb-20">
        {/* Header */}
        <header className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 z-10">
          <div className="flex items-center gap-3 p-3">
            <button
              onClick={handleBack}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-sm ${config.color}`}>{config.emoji}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {event.narrative_name || config.name}
                </span>
              </div>
            </div>
            {/* 序号 */}
            {currentIndex !== undefined && totalInCategory !== undefined && (
              <span className="text-sm text-gray-400">
                {currentIndex + 1} / {totalInCategory}
              </span>
            )}
          </div>
        </header>

        <main className="p-4 max-w-2xl mx-auto space-y-4">
          {/* 标题卡片 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
            <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-snug">
              {event.title || t('未命名事件', 'Untitled Event')}
            </h1>
          </div>

          {/* 背景 */}
          {event.background && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <span>📖</span> {t('背景', 'Background')}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                {event.background}
              </p>
            </div>
          )}

          {/* 事件详情 */}
          {event.what_happened && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <span>📝</span> {t('事件详情', 'What Happened')}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                {event.what_happened}
              </p>
            </div>
          )}

          {/* Signal Strength */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                <span>📊</span> {t('信号强度', 'Signal Strength')}
            </h2>

            {/* Mentioned By */}
            {event.mentioned_by_accounts && event.mentioned_by_accounts.length > 0 && (
              <div className="mb-3">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('提及账号', 'Mentioned by')}</p>
                <div className="flex flex-wrap gap-2">
                  {event.mentioned_by_accounts.slice(0, 10).map((account, i) => (
                    <a
                      key={i}
                      href={`https://twitter.com/${account.screen_name}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-xs hover:underline"
                    >
                      @{account.screen_name}
                      {account.layer && (
                        <span className="text-blue-400 dark:text-blue-500 text-[10px]">({account.layer})</span>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* 统计数字 */}
            <div className="grid grid-cols-4 gap-2">
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-red-600">{formatNum(event.engagement?.likes || 0)}</div>
                <div className="text-[10px] text-gray-500">Likes</div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-green-600">{formatNum(event.engagement?.retweets || 0)}</div>
                <div className="text-[10px] text-gray-500">Retweets</div>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-purple-600">{formatNum(event.engagement?.views || 0)}</div>
                <div className="text-[10px] text-gray-500">Views</div>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-blue-600">{event.total_mentions || 0}</div>
                <div className="text-[10px] text-gray-500">Mentions</div>
              </div>
            </div>
          </div>

          {/* 趋势预测 */}
          {event.trend && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <span>🔮</span> {t('趋势预测', 'Trend Forecast')}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                {event.trend}
              </p>
            </div>
          )}

          {/* 操作按钮 */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <FollowButton
              eventId={event.event_id}
              date={date}
              eventTitle={event.title || ''}
              narrativeName={event.narrative_name || event.narrative}
              eventInstanceId={event.event_instance_id}
              eventData={event as unknown as Record<string, unknown>}
              compact
            />
            <button
              type="button"
              onClick={handleOpenTimeline}
              className="flex items-center justify-center gap-1 px-2 py-2 rounded-md text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30"
            >
              <span>⏳</span>
              <span>{t('查看时间轴', 'View Timeline')}</span>
            </button>
            <button
              onClick={handleOpenExplain}
              disabled={interpretLoading}
              className="flex items-center justify-center gap-1 px-2 py-2 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              {interpretLoading ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('生成中...', 'Generating...')}
                </>
              ) : (
                <>
                  <span>💬</span>
                  {t('展开说说', 'Explain')}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleOpenPolicyAnalysis}
              className="flex items-center justify-center gap-1 px-2 py-2 rounded-md border border-blue-200 dark:border-blue-800 text-xs font-medium bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            >
              <Scale className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t('政策解读', 'Policy Analysis')}</span>
            </button>
          </div>

          {/* 关注建议入口 */}
          {event.mentioned_by_accounts && event.mentioned_by_accounts.length > 0 && (
            <Link
              to={`/event/${id}/follow-suggest?date=${date}&narrative=${narrative || ''}`}
              className="block bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-4 hover:from-blue-100 hover:to-purple-100 dark:hover:from-blue-900/30 dark:hover:to-purple-900/30 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('关注建议', 'Follow Suggestions')}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {t(
                      `基于 ${event.mentioned_by_accounts.length} 个高价值账号`,
                      `Based on ${event.mentioned_by_accounts.length} high-value accounts`
                    )}
                  </p>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          )}

          {/* 多角色解读 */}
          {interpretData && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                <span>🤖</span> {t('多角色解读', 'Role-Based Analysis')}
              </h2>
              <InterpretView interpretations={interpretData.interpretations} />
            </div>
          )}

          {/* Polymarket 匹配 */}
          {polymarketData && polymarketData.total_matches > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                <span>🔗</span> {t('预测市场', 'Prediction Markets')} ({polymarketData.total_matches})
              </h2>
              <div className="space-y-2">
                {polymarketData.polymarket_matches.slice(0, 3).map((match) => (
                  <PolymarketCard key={match.market_id} match={match} />
                ))}
              </div>
            </div>
          )}

          {/* 来源推文 */}
          {event.tweets && event.tweets.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                <span>🔗</span> {t('相关推文', 'Related Tweets')} ({event.tweets.length})
              </h2>
              <div className="space-y-2">
                {event.tweets.slice(0, 5).map((tweet) => (
                  <a
                    key={tweet.tweet_id}
                    href={getTweetLink(tweet.tweet_id, tweet.screen_name || undefined)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    {tweet.screen_name && (
                      <span className="font-medium text-blue-600 dark:text-blue-400 text-xs">@{tweet.screen_name}</span>
                    )}
                    {tweet.text && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">{tweet.text}</p>
                    )}
                  </a>
                ))}
                {event.tweets.length > 5 && (
                  <p className="text-center text-xs text-gray-400 py-2">
                    {t(
                      `还有 ${event.tweets.length - 5} 条推文...`,
                      `${event.tweets.length - 5} more tweets...`
                    )}
                  </p>
                )}
              </div>
            </div>
          )}
        </main>

        {/* 底部导航 */}
        {(canGoPrev || canGoNext) && (
          <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-3 z-10">
            <div className="flex items-center justify-between max-w-2xl mx-auto">
              <button
                onClick={() => navigateToEvent('prev')}
                disabled={!canGoPrev || navigatingDirection !== null}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm ${
                  canGoPrev && navigatingDirection === null
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                    : 'bg-gray-50 dark:bg-gray-800/50 text-gray-400 cursor-not-allowed'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {t('上一个', 'Previous')}
              </button>

              <span className="text-sm text-gray-500">
                {currentIndex !== undefined && totalInCategory !== undefined && `${currentIndex + 1} / ${totalInCategory}`}
              </span>

              <button
                onClick={() => navigateToEvent('next')}
                disabled={!canGoNext || navigatingDirection !== null}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm ${
                  canGoNext && navigatingDirection === null
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                    : 'bg-gray-50 dark:bg-gray-800/50 text-gray-400 cursor-not-allowed'
                }`}
              >
                {t('下一个', 'Next')}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </AnalyticsTracker>
  )
}
